/**
 * Utilidades robustas para el manejo de fechas
 * Soporta múltiples formatos de entrada y salida
 *
 * Uso:
 *   import { DateUtils } from 'src/app/core/utils/date.utils';
 *
 *   // Convertir a ISO para backend
 *   const iso = DateUtils.toISO('12/09/1967'); // -> '1967-09-12'
 *
 *   // Convertir a formato display (internacional)
 *   const display = DateUtils.toDisplay('1967-09-12T00:00:00'); // -> '12/09/1967'
 *
 *   // Parsear fecha
 *   const parsed = DateUtils.parse('12/09/1967'); // -> { day: 12, month: 9, year: 1967 }
 *
 *   // Validar fecha
 *   const isValid = DateUtils.isValid('31/02/2024'); // -> false
 */

export interface ParsedDate {
  day: number;
  month: number;
  year: number;
}

export interface DateParseOptions {
  /** Si true, muestra logs en consola (default: false en producción) */
  debug?: boolean;
  /** Año mínimo permitido (default: 1800) */
  minYear?: number;
  /** Año máximo permitido (default: año actual) */
  maxYear?: number;
  /** Si true, permite fechas futuras (default: true) */
  allowFuture?: boolean;
}

const DEFAULT_OPTIONS: DateParseOptions = {
  debug: true, // Cambiar a false en producción
  minYear: 1800,
  maxYear: new Date().getFullYear(),
  allowFuture: true
};

export class DateUtils {

  /**
   * Parsea una fecha de cualquier formato común y extrae día, mes, año
   *
   * Formatos soportados:
   * - ISO: yyyy-MM-dd, yyyy-MM-ddTHH:mm:ss, yyyy-MM-ddTHH:mm:ss.sssZ
   * - Internacional: dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy
   * - Americano: mm/dd/yyyy (detectado por contexto cuando día > 12)
   * - Con caracteres de máscara: limpia automáticamente _, espacios, etc.
   *
   * @param input - String de fecha en cualquier formato
   * @param options - Opciones de parseo
   * @returns ParsedDate con day, month, year o null si no se puede parsear
   */
  static parse(input: string | null | undefined, options: DateParseOptions = {}): ParsedDate | null {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const log = opts.debug ? console.log.bind(console) : () => {};
    const warn = opts.debug ? console.warn.bind(console) : () => {};

    if (!input || typeof input !== 'string') {
      warn('[DateUtils.parse] Input inválido:', input);
      return null;
    }

    // Limpiar: remover T y todo después (hora), espacios, underscores, y otros caracteres basura
    let clean = input.toString().split('T')[0].trim();
    clean = clean.replace(/[_\s]+/g, ''); // Remover underscores y espacios
    clean = clean.replace(/^[^\d]+|[^\d]+$/g, ''); // Remover caracteres no numéricos al inicio/final

    log('[DateUtils.parse] Input original:', input, '-> Limpio:', clean);

    if (!clean || clean.length < 8) {
      warn('[DateUtils.parse] Fecha muy corta después de limpiar:', clean);
      return null;
    }

    let day: number, month: number, year: number;

    // Patrón ISO: yyyy-MM-dd o yyyy/MM/dd
    const isoPattern = /^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/;
    const isoMatch = clean.match(isoPattern);

    if (isoMatch) {
      year = parseInt(isoMatch[1], 10);
      month = parseInt(isoMatch[2], 10);
      day = parseInt(isoMatch[3], 10);
      log('[DateUtils.parse] Detectado formato ISO:', { day, month, year });
    } else {
      // Patrón con separadores: dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy
      const slashPattern = /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/;
      const slashMatch = clean.match(slashPattern);

      if (slashMatch) {
        const part1 = parseInt(slashMatch[1], 10);
        const part2 = parseInt(slashMatch[2], 10);
        year = parseInt(slashMatch[3], 10);

        // Detectar si es dd/mm/yyyy o mm/dd/yyyy
        // Lógica:
        // - Si part1 > 12, definitivamente es el día (dd/mm/yyyy)
        // - Si part2 > 12, definitivamente es el día, part1 es mes (mm/dd/yyyy americano)
        // - Si ambos <= 12, asumir formato internacional (dd/mm/yyyy)
        if (part1 > 12 && part2 <= 12) {
          // Definitivamente dd/mm/yyyy (día > 12)
          day = part1;
          month = part2;
          log('[DateUtils.parse] Detectado dd/mm/yyyy (día > 12):', { day, month, year });
        } else if (part2 > 12 && part1 <= 12) {
          // Definitivamente mm/dd/yyyy americano (segundo número > 12 = día)
          month = part1;
          day = part2;
          log('[DateUtils.parse] Detectado mm/dd/yyyy americano (día en segunda posición > 12):', { day, month, year });
        } else {
          // Ambos <= 12, asumir formato internacional (dd/mm/yyyy)
          day = part1;
          month = part2;
          log('[DateUtils.parse] Asumiendo dd/mm/yyyy (ambos <= 12):', { day, month, year });
        }
      } else {
        // Intentar formato sin separadores: ddmmyyyy o yyyymmdd
        const noSepPattern1 = /^(\d{2})(\d{2})(\d{4})$/; // ddmmyyyy
        const noSepPattern2 = /^(\d{4})(\d{2})(\d{2})$/; // yyyymmdd

        const noSepMatch1 = clean.match(noSepPattern1);
        const noSepMatch2 = clean.match(noSepPattern2);

        if (noSepMatch2 && parseInt(noSepMatch2[1], 10) > 1800) {
          // yyyymmdd
          year = parseInt(noSepMatch2[1], 10);
          month = parseInt(noSepMatch2[2], 10);
          day = parseInt(noSepMatch2[3], 10);
          log('[DateUtils.parse] Detectado yyyymmdd:', { day, month, year });
        } else if (noSepMatch1) {
          // ddmmyyyy
          day = parseInt(noSepMatch1[1], 10);
          month = parseInt(noSepMatch1[2], 10);
          year = parseInt(noSepMatch1[3], 10);
          log('[DateUtils.parse] Detectado ddmmyyyy:', { day, month, year });
        } else {
          warn('[DateUtils.parse] No se pudo parsear el formato:', clean);
          return null;
        }
      }
    }

    // Validar rangos básicos
    if (month < 1 || month > 12) {
      warn('[DateUtils.parse] Mes fuera de rango (1-12):', month);
      return null;
    }
    if (day < 1 || day > 31) {
      warn('[DateUtils.parse] Día fuera de rango (1-31):', day);
      return null;
    }
    if (year < opts.minYear! || year > opts.maxYear!) {
      warn(`[DateUtils.parse] Año fuera de rango (${opts.minYear}-${opts.maxYear}):`, year);
      return null;
    }

    // Validar que la fecha sea real (ej: no 31/02/2024, no 31/04/2024)
    const testDate = new Date(year, month - 1, day);
    if (testDate.getDate() !== day || testDate.getMonth() !== month - 1 || testDate.getFullYear() !== year) {
      warn('[DateUtils.parse] Fecha no existe en el calendario:', { day, month, year });
      return null;
    }

    // Validar fecha futura si no está permitida
    if (!opts.allowFuture) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (testDate > today) {
        warn('[DateUtils.parse] Fecha futura no permitida:', { day, month, year });
        return null;
      }
    }

    return { day, month, year };
  }

  /**
   * Convierte cualquier formato de fecha a ISO (yyyy-MM-dd) para el backend
   *
   * @param date - String de fecha en cualquier formato
   * @param options - Opciones de parseo
   * @returns String en formato ISO o null si no se puede convertir
   */
  static toISO(date: string | null | undefined, options: DateParseOptions = {}): string | null {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const log = opts.debug ? console.log.bind(console) : () => {};
    const warn = opts.debug ? console.warn.bind(console) : () => {};

    const parsed = this.parse(date, options);
    if (!parsed) {
      warn('[DateUtils.toISO] No se pudo parsear la fecha:', date);
      return null;
    }

    const { day, month, year } = parsed;
    const dd = day.toString().padStart(2, '0');
    const mm = month.toString().padStart(2, '0');
    const result = `${year}-${mm}-${dd}`;

    log('[DateUtils.toISO] Resultado:', date, '->', result);
    return result;
  }

  /**
   * Convierte cualquier formato de fecha a formato display internacional (dd/mm/yyyy)
   *
   * @param date - String de fecha en cualquier formato
   * @param options - Opciones de parseo
   * @returns String en formato dd/mm/yyyy o string vacío si no se puede convertir
   */
  static toDisplay(date: string | null | undefined, options: DateParseOptions = {}): string {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const log = opts.debug ? console.log.bind(console) : () => {};
    const warn = opts.debug ? console.warn.bind(console) : () => {};

    if (!date || (typeof date === 'string' && date.trim() === '')) {
      return '';
    }

    const parsed = this.parse(date, options);
    if (!parsed) {
      warn('[DateUtils.toDisplay] No se pudo parsear, devolviendo vacío:', date);
      return '';
    }

    const { day, month, year } = parsed;
    const dd = day.toString().padStart(2, '0');
    const mm = month.toString().padStart(2, '0');
    const result = `${dd}/${mm}/${year}`;

    log('[DateUtils.toDisplay] Resultado:', date, '->', result);
    return result;
  }

  /**
   * Convierte cualquier formato de fecha a formato americano (mm/dd/yyyy)
   *
   * @param date - String de fecha en cualquier formato
   * @param options - Opciones de parseo
   * @returns String en formato mm/dd/yyyy o string vacío si no se puede convertir
   */
  static toUSFormat(date: string | null | undefined, options: DateParseOptions = {}): string {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const log = opts.debug ? console.log.bind(console) : () => {};

    if (!date || (typeof date === 'string' && date.trim() === '')) {
      return '';
    }

    const parsed = this.parse(date, options);
    if (!parsed) {
      return '';
    }

    const { day, month, year } = parsed;
    const dd = day.toString().padStart(2, '0');
    const mm = month.toString().padStart(2, '0');
    const result = `${mm}/${dd}/${year}`;

    log('[DateUtils.toUSFormat] Resultado:', date, '->', result);
    return result;
  }

  /**
   * Valida si una fecha es válida
   *
   * @param date - String de fecha en cualquier formato
   * @param options - Opciones de validación
   * @returns true si la fecha es válida
   */
  static isValid(date: string | null | undefined, options: DateParseOptions = {}): boolean {
    return this.parse(date, { ...options, debug: false }) !== null;
  }

  /**
   * Compara dos fechas
   *
   * @returns -1 si date1 < date2, 0 si son iguales, 1 si date1 > date2, null si alguna es inválida
   */
  static compare(date1: string, date2: string, options: DateParseOptions = {}): number | null {
    const p1 = this.parse(date1, { ...options, debug: false });
    const p2 = this.parse(date2, { ...options, debug: false });

    if (!p1 || !p2) return null;

    const d1 = new Date(p1.year, p1.month - 1, p1.day);
    const d2 = new Date(p2.year, p2.month - 1, p2.day);

    if (d1 < d2) return -1;
    if (d1 > d2) return 1;
    return 0;
  }

  /**
   * Obtiene la fecha actual en formato ISO
   */
  static todayISO(): string {
    const today = new Date();
    const dd = today.getDate().toString().padStart(2, '0');
    const mm = (today.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = today.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  }

  /**
   * Obtiene la fecha actual en formato display (dd/mm/yyyy)
   */
  static todayDisplay(): string {
    const today = new Date();
    const dd = today.getDate().toString().padStart(2, '0');
    const mm = (today.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = today.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  /**
   * Calcula la edad a partir de una fecha de nacimiento
   *
   * @param birthDate - Fecha de nacimiento en cualquier formato
   * @returns Edad en años o null si la fecha es inválida
   */
  static calculateAge(birthDate: string | null | undefined, options: DateParseOptions = {}): number | null {
    const parsed = this.parse(birthDate, { ...options, debug: false });
    if (!parsed) return null;

    const today = new Date();
    const birth = new Date(parsed.year, parsed.month - 1, parsed.day);

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  }
}

